import { NotificationEntity } from '@/components/Classes/NotificationEntity';
import { createBackendApiInstance } from '@/services/APIs/ApiInstanceCreator';
import { NOTIFICATION_ENDPOINTS } from '@/services/APIs/endpoints';

const api = createBackendApiInstance({
  controller: NOTIFICATION_ENDPOINTS.CONTROLLER,
});

/**
 * Gets all notifications for the current user.
 * @returns {Promise<NotificationEntity[]>} Array of notifications or empty array on error.
 */
export async function getUserNotifications(): Promise<NotificationEntity[]> {
  try {
    const resp = await api.get<NotificationEntity[]>(NOTIFICATION_ENDPOINTS.GET_USER_NOTIFICATIONS);
    if (!resp.ok || !resp.data) return [];
    if (!Array.isArray(resp.data)) return [];
    return resp.data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }
}

/**
 * Marks a specific notification as read.
 * @param {string} notificationId - The notification ID.
 * @returns {Promise<void>}
 */
export async function markAsRead(notificationId: string): Promise<void> {
  await api.put(NOTIFICATION_ENDPOINTS.MARK_AS_READ.replace(':notificationId', notificationId));
}

/**
 * Deletes a specific notification.
 * @param {string} notificationId - The notification ID.
 * @returns {Promise<void>}
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  await api.delete(NOTIFICATION_ENDPOINTS.DELETE_NOTIFICATION.replace(':notificationId', notificationId));
}

/**
 * Creates a new notification.
 * @param {Omit<NotificationEntity, 'id' | 'createdAt' | 'isRead'>} notification - The notification data.
 * @returns {Promise<NotificationEntity | null>} The created notification or null on error.
 */
export async function createNotification(
  notification: Omit<NotificationEntity, 'id' | 'createdAt' | 'isRead'>
): Promise<NotificationEntity | null> {
  const resp = await api.post<NotificationEntity>(NOTIFICATION_ENDPOINTS.CREATE_NOTIFICATION, notification);
  if (!resp.ok) {
    console.error('Failed to create notification:', resp);
    return null;
  }
  return resp.data as NotificationEntity;
}

/**
 * Marks all notifications as read for the current user.
 * @returns {Promise<void>}
 */
export async function markAllAsRead(): Promise<void> {
  await api.post(NOTIFICATION_ENDPOINTS.MARK_ALL_AS_READ);
}

/**
 * Notification service (for backward compatibility if needed).
 * Exports all functions as methods on an object.
 */
export const notificationService = {
  getUserNotifications,
  markAsRead,
  deleteNotification,
  createNotification,
  markAllAsRead,
};
